import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { X, Download, Upload, Eye, CheckCircle, XCircle, AlertCircle, AlertTriangle, ArrowRightLeft, PlusCircle } from 'lucide-react';
import apiClient from '../../../lib/api-client';
import { getImportErrorMessage } from '../../../lib/import-error';
import ImportNewClasses, { ImportNewClass } from './ImportNewClasses';

interface ImportStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  schoolId: string;
  schoolName: string;
  onImportSuccess: () => void;
}

interface PreviewData {
  row: number;
  ten_hoc_sinh: string;
  ngay_sinh: string;
  khoi: string;
  ma_hoc_sinh: string;
  lop: string;
  status: string;
}

interface ImportError {
  row: number;
  error: string;
  data: any | null;
}

const PREVIEW_STATUS: Record<string, { label: string; className: string; Icon: typeof CheckCircle }> = {
  valid: {
    label: 'Hợp lệ',
    className: 'bg-green-100 text-green-800',
    Icon: CheckCircle
  },
  exists: {
    label: 'Đã có trong trường',
    className: 'bg-blue-100 text-blue-800',
    Icon: AlertCircle
  },
  class_change: {
    label: 'Sẽ chuyển lớp',
    className: 'bg-orange-100 text-orange-800',
    Icon: ArrowRightLeft
  },
  class_new: {
    label: 'Lớp mới',
    className: 'bg-indigo-100 text-indigo-800',
    Icon: PlusCircle
  },
  class_not_found: {
    label: 'Không tìm thấy lớp',
    className: 'bg-yellow-100 text-yellow-800',
    Icon: AlertTriangle
  }
};

const PreviewStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const { label, className, Icon } = PREVIEW_STATUS[status] ?? PREVIEW_STATUS.valid;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}>
      <Icon className="h-3 w-3 mr-1" />
      {label}
    </span>
  );
};

const ImportStudentModal: React.FC<ImportStudentModalProps> = ({
  isOpen,
  onClose,
  schoolId,
  schoolName,
  onImportSuccess
}) => {
  const [step, setStep] = useState<'upload' | 'preview' | 'importing' | 'complete'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<PreviewData[]>([]);
  const [previewErrors, setPreviewErrors] = useState<ImportError[]>([]);
  const [importErrors, setImportErrors] = useState<ImportError[]>([]);
  const [importedData, setImportedData] = useState<any[]>([]);
  const [newClasses, setNewClasses] = useState<ImportNewClass[]>([]);
  const [createdClasses, setCreatedClasses] = useState<ImportNewClass[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  });

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient.get('/schooluser/download-template', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'student_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download template:', error);
    }
  };

  const handlePreview = async () => {
    if (!file) return;

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('schoolId', schoolId);

      const response = await apiClient.post('/schooluser/preview-import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const result = response.data;
      
      if (result.success) {
        setPreviewData(result.data || []);
        setPreviewErrors(result.errors || []);
        setNewClasses(result.new_classes || []);
        setStep('preview');
      } else {
        // Xử lý trường hợp preview thất bại
        setPreviewData([]);
        setNewClasses([]);
        setPreviewErrors(result.errors || [{
          row: 0,
          error: result.message || 'Có lỗi xảy ra khi xem trước dữ liệu',
          data: null
        }]);
        setStep('preview');
      }
    } catch (error: any) {
      console.error('Preview failed:', error);
      // Xử lý lỗi network
      setPreviewData([]);
      setNewClasses([]);
      setPreviewErrors([{
        row: 0,
        error: getImportErrorMessage(error),
        data: null
      }]);
      setStep('preview');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setIsLoading(true);
    setStep('importing');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('schoolId', schoolId);
      formData.append('schoolName', schoolName);

      const response = await apiClient.post('/schooluser/import-students', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const result = response.data;
      
      // Xử lý cả trường hợp thành công và thất bại
      setImportedData(result.imported || []);
      setImportErrors(result.errors || []);
      setCreatedClasses(result.created_classes || []);
      setStep('complete');
      
      // Chỉ gọi onImportSuccess khi thực sự thành công
      if (result.success) {
        onImportSuccess();
      }
    } catch (error: any) {
      console.error('Import failed:', error);
      // Xử lý lỗi network hoặc server error
      setImportErrors([{
        row: 0,
        error: getImportErrorMessage(error),
        data: null
      }]);
      setImportedData([]);
      setCreatedClasses([]);
      setStep('complete');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreviewData([]);
    setPreviewErrors([]);
    setImportErrors([]);
    setImportedData([]);
    setNewClasses([]);
    setCreatedClasses([]);
    setStep('upload');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Import học sinh từ Excel</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {step === 'upload' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Tải lên file Excel (.xlsx) để import học sinh
                </h3>
                <p className="text-gray-600">
                  File cần có các cột: Tên học sinh, Ngày sinh, Lớp (Khối nếu có) — thừa cột STT, Trường cũng không sao. Lớp chưa có trong trường sẽ được tạo cho năm học hiện tại
                </p>
                <p className="text-sm text-blue-600 mt-2">
                  💡 Tải file mẫu Excel bên dưới để có định dạng chính xác
                </p>
              </div>

              {/* Download Template */}
              <div className="text-center">
                <button
                  onClick={handleDownloadTemplate}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Tải file mẫu (Excel)
                </button>
              </div>

              {/* File Upload */}
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                {file ? (
                  <div>
                    <p className="text-sm text-gray-600">File đã chọn:</p>
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-600">
                      {isDragActive
                        ? 'Thả file vào đây...'
                        : 'Kéo thả file Excel vào đây hoặc click để chọn file'}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Hỗ trợ file .xlsx, .xls
                    </p>
                  </div>
                )}
              </div>

              {/* Preview Button */}
              {file && (
                <div className="text-center">
                  <button
                    onClick={handlePreview}
                    disabled={isLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    {isLoading ? 'Đang xử lý...' : 'Xem trước'}
                  </button>
                </div>
              )}
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Xem trước dữ liệu import
                </h3>
                <p className="text-gray-600">
                  Kiểm tra dữ liệu trước khi import
                </p>
              </div>

              {/* Preview Table - Only this part scrolls */}
              <div className="overflow-y-auto max-h-96">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Dòng
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tên học sinh
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ngày sinh
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Mã học sinh
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Khối
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Lớp
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Trạng thái
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {previewData.map((row) => (
                        <tr key={row.row} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.row}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.ten_hoc_sinh}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.ngay_sinh}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.ma_hoc_sinh}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.khoi || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.lop}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <PreviewStatusBadge status={row.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <ImportNewClasses classes={newClasses} />

              {/* Errors */}
              {previewErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <h4 className="text-sm font-medium text-red-800 mb-2">
                    Có {previewErrors.length} lỗi cần sửa:
                  </h4>
                  <div className="space-y-3">
                    {previewErrors.map((error, index) => (
                      <div key={index} className="bg-white border border-red-200 rounded-md p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-red-800 mb-1">
                              {error.row > 0 && `Dòng ${error.row}: `}{error.error}
                            </p>
                            {error.data && (
                              <div className="text-xs text-gray-600 mt-2">
                                <p className="font-medium mb-1">Dữ liệu gốc:</p>
                                <div className="grid grid-cols-2 gap-2">
                                  <div><span className="font-medium">A:</span> {error.data.A || '-'}</div>
                                  <div><span className="font-medium">B:</span> {error.data.B || '-'}</div>
                                  <div><span className="font-medium">C:</span> {error.data.C || '-'}</div>
                                </div>
                              </div>
                            )}
                          </div>
                          <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 ml-2" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between">
                <button
                  onClick={() => setStep('upload')}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Quay lại
                </button>
                <button
                  onClick={handleImport}
                  disabled={isLoading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {isLoading ? 'Đang import...' : 'Import'}
                </button>
              </div>
            </div>
          )}

          {step === 'importing' && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Đang import dữ liệu...
              </h3>
              <p className="text-gray-600">
                Vui lòng đợi trong khi hệ thống xử lý file
              </p>
            </div>
          )}

          {step === 'complete' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Import hoàn tất
                </h3>
                <p className="text-gray-600">
                  {importedData.length} học sinh đã được import thành công
                </p>
              </div>

              {/* Scrollable Content Area */}
              <div className="overflow-y-auto max-h-96">
                <div className="mb-4">
                  <ImportNewClasses classes={createdClasses} created />
                </div>

                {/* Success Summary */}
                {importedData.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                    <h4 className="text-sm font-medium text-green-800 mb-2">
                      Import thành công:
                    </h4>
                    <ul className="text-sm text-green-700 space-y-1">
                      {importedData.map((item, index) => (
                        <li key={index}>
                          Dòng {item.row}: {item.student.name} - {item.student.user_name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Error Summary */}
                {importErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <h4 className="text-sm font-medium text-red-800 mb-2">
                      Có {importErrors.length} lỗi:
                    </h4>
                    <div className="space-y-3">
                      {importErrors.map((error, index) => (
                        <div key={index} className="bg-white border border-red-200 rounded-md p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-red-800 mb-1">
                                {error.row > 0 && `Dòng ${error.row}: `}{error.error}
                              </p>
                              {error.data && (
                                <div className="text-xs text-gray-600 mt-2">
                                  <p className="font-medium mb-1">Dữ liệu gốc:</p>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div><span className="font-medium">A:</span> {error.data.A || '-'}</div>
                                    <div><span className="font-medium">B:</span> {error.data.B || '-'}</div>
                                    <div><span className="font-medium">C:</span> {error.data.C || '-'}</div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 ml-2" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center space-x-4">
                <button
                  onClick={handleReset}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Import file khác
                </button>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Hoàn tất
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportStudentModal;
